import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import type { LightenDays, Org } from '../../lib/api';
import { Button, Checkbox, Dialog, Select } from '../../ui';
import { DialogClose } from '../../ui/DialogClose';
import { useInvalidateContent } from '../dashboard/queries';
import { formatBytes } from './format';

/**
 * "Lighten" (P16-4 Ruling 11): old automatic versions go now, images nothing uses any more enter the GC's 14-day
 * grace. The preview counts what would go before anything is done; admins and the owner confirm, members only look.
 */
export function LightenDialog({ org, onClose }: { org: Org; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const [days, setDays] = useState<LightenDays>(30);
  const [versions, setVersions] = useState(true);
  const [images, setImages] = useState(true);
  const [result, setResult] = useState<{ versionCount: number; orphanCount: number } | null>(null);
  const [error, setError] = useState(false);
  const [running, setRunning] = useState(false);
  const admin = org.role !== 'member';
  const preview = useQuery({ queryKey: ['lighten', org.id, days, versions, result], queryFn: () => api.lightenPreview(org.id, days, versions) });
  const size = (n: number) => formatBytes(n, i18n.language);
  const p = preview.data;
  const nothing = p !== undefined && (versions ? p.versionCount : 0) + (images ? p.orphanCount : 0) === 0;

  const run = async () => {
    setError(false);
    setRunning(true);
    try {
      setResult(await api.lighten(org.id, { days, versions, images }));
      await invalidate();
    } catch {
      setError(true);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog
      title={t('settings.lighten.title')}
      description={t('settings.lighten.hint')}
      onClose={onClose}
      footer={
        <>
          <DialogClose>{t('sharing.close')}</DialogClose>
          {admin && (
            <Button variant="primary" disabled={!p || nothing || running} onClick={() => void run()}>
              {t('settings.lighten.run')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4 font-sans text-sm text-fg">
        <label className="flex flex-col gap-1 font-medium">
          {t('settings.lighten.olderThan')}
          <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value) as LightenDays)}>
            {([7, 30, 90] as const).map((d) => (
              <option key={d} value={d}>
                {t('settings.lighten.days', { count: d })}
              </option>
            ))}
          </Select>
        </label>
        <div className="flex flex-col gap-1">
          <Checkbox label={t('settings.lighten.versions')} checked={versions} onCheckedChange={setVersions} />
          {p && versions && <p className="ml-6 font-mono text-xs text-fg-muted">{t('settings.lighten.versionsPreview', { count: p.versionCount, size: size(p.versionBytes) })}</p>}
        </div>
        <div className="flex flex-col gap-1">
          <Checkbox label={t('settings.lighten.images')} checked={images} onCheckedChange={setImages} />
          {p && images && <p className="ml-6 font-mono text-xs text-fg-muted">{t('settings.lighten.imagesPreview', { count: p.orphanCount, size: size(p.orphanBytes) })}</p>}
        </div>
        {nothing && !result && <p className="text-fg-muted">{t('settings.lighten.nothing')}</p>}
        {!admin && <p className="text-fg-muted">{t('settings.lighten.adminsOnly')}</p>}
        {result && (
          <p role="status">
            {t('settings.lighten.done', {
              versions: t('settings.lighten.versionsDone', { count: result.versionCount }),
              images: t('settings.lighten.imagesDone', { count: result.orphanCount }),
            })}
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-danger">
            {t('auth.genericError')}
          </p>
        )}
      </div>
    </Dialog>
  );
}
